import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { LayoutService } from '../../core/services/layout.service';
import { PermissionService } from '../../core/services/permission.service';
import { environment } from '../../../environments/environment';
import { HelpDeskDataService } from '../../features/helpdesk/helpdesk-data.service';
import { ProductModeService } from '../../core/services/product-mode.service';
import { TasksTerminologyService } from '../../core/services/tasks-terminology.service';
import { SitesTerminologyService } from '../../core/services/sites-terminology.service';
import { ListGroupsTerminologyService } from '../../core/services/list-groups-terminology.service';
import { ModuleTerminologyService } from '../../core/services/module-terminology.service';
import { ModuleSettingsStore } from '../../features/module-settings/module-settings.store';
import { SettingsModuleKey } from '../../features/module-settings/module-settings.registry';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  /** Module code (matches Modules.Code in the DB). Item is shown only when the tenant has it enabled. */
  module: string;
  /** Read permission for the module; when set, the user's role must have it for the item to show. */
  permission?: string;
  /** Always show regardless of module/permission (e.g. Dashboard, a safe home). */
  always?: boolean;
  /** Production-ready. When environment.productionMenu is true, ONLY these items show. */
  prod?: boolean;
  /** Product this item is exclusive to. 'os' = only in SiteWareOS mode (e.g. Sites);
   *  'str' = only in SiteWareStr mode (Properties, List Groups). Omitted = COMMON (both modes). */
  product?: 'os' | 'str';
  badge?: number;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

@Component({
  selector: 'sw-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  private readonly layout = inject(LayoutService);
  private readonly permission = inject(PermissionService);
  private readonly helpdesk = inject(HelpDeskDataService);
  private readonly productMode = inject(ProductModeService);
  private readonly tasksTerm = inject(TasksTerminologyService);
  private readonly sitesTerm = inject(SitesTerminologyService);
  private readonly moduleTerms = inject(ModuleTerminologyService);
  private readonly listGroupsTerm = inject(ListGroupsTerminologyService);
  /** Menu labels for the modules named in their own Settings → Module Terminology. */
  private readonly moduleNav = (() => {
    const store = inject(ModuleSettingsStore);
    const nav = (key: SettingsModuleKey) => { const t = store.terms(key); return computed(() => t().navLabel); };
    return { audits: nav('audits'), work_orders: nav('work_orders'), work_requests: nav('work_requests'), vendors: nav('vendors'), parts: nav('parts'), assets: nav('assets') };
  })();
  readonly collapsed = this.layout.sidebarCollapsed;
  readonly mobileOpen = this.layout.mobileDrawerOpen;

  /** Collapsed sidebar: the hovered item's name and where to show it (vertical centre of the item). */
  readonly tip = signal<{ label: string; top: number } | null>(null);

  showTip(e: MouseEvent, label: string) {
    // Only the desktop icon rail: below 1024px the sidebar is a full-width drawer.
    if (!this.collapsed() || window.matchMedia('(max-width: 1024px)').matches) return;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.tip.set({ label, top: r.top + r.height / 2 });
  }

  hideTip() { this.tip.set(null); }

  // Full module menu. Items still show only when the tenant has the module enabled AND the
  // user's role can read it, so this list is the superset — the permission/module gate trims it.
  // Built fresh inside the computed() below (not a static field) so the Help Desk label can stay
  // in sync with its configurable nav label in Settings.
  private buildAllGroups(): NavGroup[] {
    return [
    {
      label: 'OVERVIEW',
      items: [
        { label: 'Dashboard', icon: 'grid', route: '/dashboard', module: 'dashboard', always: true, prod: true },
      ]
    },
    {
      label: 'OPERATIONS',
      items: [
        { label: this.tasksTerm.terminology().nav_label, icon: 'check-square', route: '/tasks', module: 'tasks', permission: 'tasks.read', prod: true },
        // Sites is the OS equivalent of STR's Properties; the two are mutually exclusive by product
        // mode. Properties and List Groups are STR-only. Property Groups and Property Tags are
        // NOT listed here — they are tabs inside the Properties screen.
        { label: this.sitesTerm.terminology().nav_label, icon: 'map-pin', route: '/sites', module: 'sites', always: true, product: 'os', prod: true },
        // Label follows the terminology set in Properties → Settings.
        { label: this.moduleTerms.get('properties').nav_label, icon: 'map-pin', route: '/properties', module: 'properties', always: true, product: 'str', prod: true },
        { label: this.listGroupsTerm.terminology().nav_label, icon: 'grid', route: '/list-groups', module: 'list_groups', always: true, product: 'str', prod: true },
        { label: this.moduleNav.audits(), icon: 'clipboard-list', route: '/audits', module: 'audits', permission: 'audits.read' },
        { label: this.moduleTerms.get('walkthroughs').nav_label, icon: 'navigation', route: '/walkthroughs', module: 'walkthroughs', permission: 'walkthroughs.read' },
        { label: 'Incidents', icon: 'alert-triangle', route: '/incidents', module: 'incidents', permission: 'incidents.read' },
        { label: this.moduleNav.work_orders(), icon: 'tool', route: '/work-orders', module: 'work_orders', permission: 'work_orders.read' },
        // Frontend-only for now, like Help Desk: always visible, not production-ready (no `prod`).
        { label: this.moduleNav.work_requests(), icon: 'work-request', route: '/work-requests', module: 'work_requests', always: true },
        // Frontend-only for now: always visible, not yet gated by a real backend module/permission
        // (and not production-ready, so no `prod: true`).
        { label: this.helpdesk.terminology().navLabel, icon: 'help-desk', route: '/helpdesk', module: 'helpdesk', always: true },
      ]
    },
    {
      label: 'ASSET MANAGEMENT',
      items: [
        // Frontend-only for now, like Work Requests: always visible, not production-ready (no `prod`).
        { label: this.moduleNav.assets(), icon: 'box', route: '/assets', module: 'assets', always: true },
        // Frontend-only for now, like Assets.
        { label: this.moduleNav.vendors(), icon: 'truck', route: '/vendors', module: 'vendors', always: true },
        { label: this.moduleNav.parts(), icon: 'nut', route: '/parts', module: 'parts', always: true },
      ]
    },
    {
      label: 'PEOPLE & CRM',
      items: [
        { label: 'CRM', icon: 'users', route: '/crm', module: 'crm', permission: 'crm.contacts.read', prod: true },
        { label: 'Marketing', icon: 'mail', route: '/marketing', module: 'marketing', permission: 'marketing.read' },
        { label: 'Payments', icon: 'credit-card', route: '/payments', module: 'payments', permission: 'payments.read' },
      ]
    },
    {
      label: 'KNOWLEDGE & DOCS',
      items: [
        { label: 'SOP', icon: 'book-open', route: '/sop', module: 'sop', permission: 'sop.read' },
        { label: 'Knowledge Base', icon: 'library', route: '/knowledge-base', module: 'knowledge_base', permission: 'knowledge_base.read' },
        { label: 'Documents', icon: 'folder', route: '/documents', module: 'documents', permission: 'documents.read' },
        { label: 'Workspaces', icon: 'layout-grid', route: '/workspaces', module: 'workspaces', permission: 'workspaces.read' },
        { label: 'Chat', icon: 'message-circle', route: '/chat', module: 'chat', permission: 'chat.read' },
        { label: 'Reports', icon: 'bar-chart-2', route: '/reports', module: 'reports', permission: 'reports.read' },
      ]
    },
    {
      label: 'ADMIN',
      items: [
        { label: 'Settings', icon: 'settings', route: '/settings', module: 'settings', permission: 'settings.read', prod: true },
        { label: 'User Management', icon: 'user-key', route: '/users', module: 'users', permission: 'users.read', prod: true },
        // Companies management (tenant admin adds additional companies). Shown to admins in both modes.
        { label: 'Companies', icon: 'building', route: '/companies-admin', module: 'users', permission: 'users.read', prod: true },
        { label: 'Activity Log', icon: 'activity', route: '/activity-log', module: 'users', permission: 'users.activity_log', prod: true },
      ]
    }
    ];
  }

  /** Show an item only when the tenant has the module enabled AND the user's role can read it.
   *  In production-menu mode, additionally restrict to items flagged `prod: true`. */
  private canSee(i: NavItem): boolean {
    // Product-mode gate: hide an item only if it's exclusive to the OTHER product.
    // Untagged items are common and show in both modes. Only the Sites ⇄ Properties/List Groups
    // pair differs: Sites is 'os'-only, Properties/List Groups are 'str'-only.
    if (i.product && i.product !== this.productMode.mode()) return false;

    // Production build exposes only the finished modules, regardless of `always`/module/permission.
    if (environment.productionMenu && !i.prod) return false;
    if (i.always) return true;
    return this.permission.hasModule(i.module) && (!i.permission || this.permission.has(i.permission));
  }

  /** Only items the user can access; groups left empty are dropped. Reacts to product mode. */
  readonly navGroups = computed<NavGroup[]>(() =>
    this.buildAllGroups()
      .map(g => ({ ...g, items: g.items.filter(i => this.canSee(i)) }))
      .filter(g => g.items.length > 0)
  );

  closeMobile() { this.layout.closeMobileDrawer(); }

  getIconPath(icon: string): string {
    const icons: Record<string, string> = {
      'grid': 'M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm11 0h7v7h-7v-7z',
      'map-pin': 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
      'check-square': 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
      'clipboard-list': 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2 M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2 M9 12h6 M9 16h6',
      'navigation': 'M3 11l19-9-9 19-2-8-8-2z',
      'alert-triangle': 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01',
      'tool': 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
      // 3D box (package) — Assets.
      'box': 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.27 6.96 12 12.01l8.73-5.05 M12 22.08V12',
      // Hex nut — used for Parts.
      'nut': 'M12 2l8.66 5v10L12 22l-8.66-5V7z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
      // Delivery truck — used for Vendors.
      'truck': 'M1 3h15v13H1z M16 8h4l3 3v5h-7z M5.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z M18.5 21a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
      // Clipboard with a question mark — a request for work, distinct from the Work Orders wrench.
      'work-request': 'M9 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2 M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1z M10 11a2 2 0 1 1 2.8 1.83c-.48.2-.8.67-.8 1.17v.5 M12 17.5h.01',
      'help-desk':'M12 2 3 6.5v6.75c0 4.68 3.36 9.06 9 10.75 5.64-1.69 9-6.07 9-10.75V6.5L12 2z M9 11.5l2 2 4-4',
      'users': 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
      'mail': 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
      'credit-card': 'M1 4h22v16H1z M1 10h22',
      'book-open': 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
      'library': 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z M6 8h.01 M6 12h.01',
      'folder': 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z',
      'layout-grid': 'M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm11 0h7v7h-7v-7z',
      'message-circle': 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
      'bar-chart-2': 'M18 20V10 M12 20V4 M6 20v-6',
      'settings': 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
      'shield': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
      'user-cog': 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0 M19.43 12.01c.11-.32.17-.67.17-1.01s-.06-.69-.17-1.01l1.09-.9a.4.4 0 0 0 .1-.49l-1.03-1.79a.4.4 0 0 0-.48-.16l-1.28.52a3.76 3.76 0 0 0-.87-.5l-.19-1.36A.39.39 0 0 0 16.38 5h-2.06a.39.39 0 0 0-.39.31l-.19 1.36c-.32.13-.61.3-.88.5l-1.27-.52a.4.4 0 0 0-.49.16l-1.03 1.79a.39.39 0 0 0 .1.49l1.09.9c-.11.32-.17.66-.17 1.01s.06.69.17 1.01l-1.09.9a.4.4 0 0 0-.1.49l1.03 1.79c.1.18.31.25.49.16l1.27-.52c.27.2.56.37.88.5l.19 1.36c.05.18.21.31.39.31h2.06c.18 0 .34-.13.38-.31l.19-1.36c.32-.13.61-.3.87-.5l1.28.52c.17.07.38 0 .48-.16l1.03-1.79a.4.4 0 0 0-.1-.49l-1.09-.9z M18 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
      // A single user (left) plus a key (right): reads as account/access management and stays
      // distinct from the CRM "users" people icon at sidebar size.
      'user-key': 'M10 15H7a4 4 0 0 0-4 4v2 M10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0 M16.5 18a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M22 22l-2.5-2.5 M18.5 15.5l-1 1',
      // Office building with windows — used for the Companies menu item.
      'building': 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16 M15 9h4a1 1 0 0 1 1 1v11 M2 21h20 M7 8h1 M11 8h1 M7 12h1 M11 12h1 M7 16h1 M11 16h1 M18 13h.5 M18 17h.5',
      // Clock with a back-arrow (history) — used for the Activity Log menu item.
      'activity': 'M3 12a9 9 0 1 0 3-6.7L3 8 M3 3v5h5 M12 7v5l3 2',
      // Tag/label — used for the Property Tags menu item.
      'tag': 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z M7 7h.01',
    };
    return icons[icon] || 'M12 12h.01';
  }
}
